import { Reveal, MaskLines } from "./Reveal";
import { Search, Hammer, TrendingUp } from "lucide-react";

const STEPS = [
  {
    icon: Search,
    step: "01",
    title: "Diagnose",
    body: "We pinpoint exactly where your process breaks under pressure — the A+ setups you talk yourself out of, the size you can't hold, the winners you cut and the losers you nurse. Most six-figure traders don't have a strategy gap. They have an execution gap, and it hides in plain sight.",
  },
  {
    icon: Hammer,
    step: "02",
    title: "Rebuild",
    body: "Then we rebuild at an identity level — not just a tighter, rules-based playbook, but your relationship with yourself as a trader, so discipline, confidence and consistency become natural instead of something you force through willpower.",
  },
  {
    icon: TrendingUp,
    step: "03",
    title: "Scale",
    body: "With execution fixed, we scale deliberately — larger size, cleaner entries and exits, and the psychology to hold real conviction when serious money is on the line. This is the stage where a 6 figure stock trading coach earns her keep many times over.",
  },
];

export const Manifesto = () => (
  <section id="approach" className="relative border-t border-white/10 py-20 sm:py-24">
    <div
      className="pointer-events-none absolute right-1/4 top-10 h-[45vh] w-[45vh] rounded-full opacity-[0.1] blur-3xl"
      style={{ background: "radial-gradient(circle, #d4af37, transparent 65%)" }}
    />
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">
            The Approach
          </span>
        </Reveal>
        <h2 className="mt-8 font-['Playfair_Display'] text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
          <MaskLines whileInView lineClass="gold-metallic-text" lines={["Knowing more isn't the edge.", "Executing better is."]} />
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-7 text-base leading-relaxed text-[#a1a1aa] sm:text-lg">
            You&rsquo;ve already read the books, bought the courses and followed the accounts. The gap
            between what you <em className="not-italic text-[#f5f5f0]">know</em> and what you actually{" "}
            <em className="not-italic text-[#f5f5f0]">do</em> under pressure is where six-figure traders
            get stuck &mdash; and it&rsquo;s precisely the gap Shabz is built to close. Her coaching
            follows a clear, repeatable path.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <Reveal key={s.step} delay={i * 0.1}>
            <div
              data-testid={`approach-step-${i}`}
              className="gold-toptrim flex h-full flex-col gap-5 bg-[#0e0e10] p-9 transition-colors duration-300 hover:bg-[#141416]"
            >
              <div className="flex items-center justify-between">
                <s.icon className="h-7 w-7 text-[#d4af37]" strokeWidth={1.3} />
                <span className="font-['IBM_Plex_Mono'] text-sm text-[#d4af37]/70">{s.step}</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl text-[#f5f5f0]">{s.title}</h3>
              <p className="text-sm leading-relaxed text-[#a1a1aa]">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
